// Billing — plan grid + currency/interval switcher + current subscription +
// invoices. Serves both parents (family plans) and school admins (school plans);
// audience is derived from the caller's role. docs/replan-v2.md R6′.
//
// Checkout redirects to the provider's hosted page (the MockProvider page today).
// On return the API's ?checkout= query drives a small success/failure banner and
// the subscription query refetches so the state reflects the webhook.
//
// 🔴 M7e §4.2: the API sets `?checkout=return|cancel`; the mock checkout page
// then appends its OWN `?outcome=` on top. This screen used to read `outcome`
// only, so it appeared to work purely by the mock's accident — real SkipCash
// sends `checkout=` alone and the banner would silently never appear. We read
// `checkout` as the contract, keep `outcome` as a mock-compat fallback, and
// strip the query after reading so a refresh cannot re-show a stale banner or
// re-arm the 3s webhook poll.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Badge, Button, Card, CardBody, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { invoiceTone } from './$locale.billing.invoices.$invoiceId';

import { PaymentMethodsCard } from '~/components/billing/PaymentMethodsCard';
import { TransactionsList } from '~/components/billing/TransactionsList';
import { Money } from '~/components/Money';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import {
  CURRENCIES,
  cancelSubscription,
  defaultCurrency,
  getSubscription,
  listPlans,
  resumeSubscription,
  startCheckout,
  type Currency,
  type Interval,
  type Invoice,
  type Plan,
} from '~/lib/billing';
import { asLocale } from '~/lib/locale';
import { formatMinorAmount } from '~/lib/money';
import { shellForContext } from '~/lib/roles';

/**
 * Read the return-redirect query ONCE, so it can be stripped straight after.
 *
 * `checkout` is the API's contract (`return` | `cancel`); `outcome` is what the
 * mock checkout page appends on top and is accepted only so the mock keeps
 * working unchanged. `card` is the card-update return (`updated`).
 */
function readReturnParams(): { checkout: 'return' | 'cancel' | null; card: string | null } {
  if (typeof window === 'undefined') return { checkout: null, card: null };
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('checkout') ?? params.get('outcome');
  // Normalise the mock's vocabulary (ok/fail/cancel) onto the API's.
  const checkout =
    raw === 'ok' || raw === 'return'
      ? ('return' as const)
      : raw === 'fail' || raw === 'cancel'
        ? ('cancel' as const)
        : null;
  return { checkout, card: params.get('card') };
}

export const Route = createFileRoute('/$locale/billing')({
  component: BillingScreen,
});

function BillingScreen() {
  const t = useT();
  const qc = useQueryClient();
  const { currentRoleContext } = useAuth();
  const audience = shellForContext(currentRoleContext) === 'school_admin' ? 'school' : 'family';
  const [currency, setCurrency] = useState<Currency>(defaultCurrency());
  const [interval, setInterval] = useState<Interval>('month');
  const [couponCode, setCouponCode] = useState('');

  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);

  // Captured once on mount; the query string is stripped below, so a refresh
  // neither re-shows the banner nor re-arms the webhook poll.
  const [returned] = useState(readReturnParams);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!returned.checkout && !returned.card) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    url.searchParams.delete('outcome');
    url.searchParams.delete('card');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [returned]);

  // The card-update return means the webhook has stored a new card; refetch.
  useEffect(() => {
    if (returned.card === 'updated') {
      void qc.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    }
  }, [returned.card, qc]);

  const plansQuery = useQuery({
    queryKey: ['billing', 'plans', audience],
    queryFn: ({ signal }) => listPlans(audience, signal),
  });
  const subQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: ({ signal }) => getSubscription(signal),
    // Refetch a couple of times after returning from checkout so the webhook lands.
    refetchInterval: returned.checkout === 'return' ? 3000 : false,
  });

  const checkout = useMutation({
    mutationFn: (input: {
      planId: string;
      currency: Currency;
      interval: Interval;
      couponCode?: string;
    }) => startCheckout(input),
    onSuccess: (res) => {
      window.location.href = res.checkoutUrl;
    },
  });

  // Normalize the coupon: trim + uppercase; undefined when empty.
  const coupon = couponCode.trim() ? couponCode.trim().toUpperCase() : undefined;

  const cancel = useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'subscription'] }),
  });
  const resume = useMutation({
    mutationFn: () => resumeSubscription(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'subscription'] }),
  });

  const sub = subQuery.data?.subscription ?? null;
  const invoices = subQuery.data?.invoices ?? [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.billing.title')}</h1>

      {returned.checkout === 'return' && (
        <Alert tone="success" className="mt-4">
          {t('app.billing.checkout_success')}
        </Alert>
      )}
      {returned.checkout === 'cancel' && (
        <Alert tone="warning" className="mt-4">
          {t('app.billing.checkout_canceled')}
        </Alert>
      )}
      {returned.card === 'updated' && (
        <Alert tone="info" className="mt-4">
          {t('app.billing.card_updated')}
        </Alert>
      )}
      {checkout.isError && (
        <Alert tone="danger" className="mt-4">
          {checkout.error instanceof ApiError
            ? checkout.error.message
            : t('app.common.error_generic')}
        </Alert>
      )}

      {/* Current subscription / manage */}
      <div className="mt-6">
        {subQuery.isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : sub ? (
          <CurrentPlanCard
            planNameKey={sub.planNameKey}
            status={sub.status}
            periodEnd={sub.currentPeriodEnd}
            cancelAtPeriodEnd={sub.cancelAtPeriodEnd}
            onCancel={() => cancel.mutate()}
            onResume={() => resume.mutate()}
            busy={cancel.isPending || resume.isPending}
          />
        ) : (
          <Card>
            <CardBody>
              <p className="text-ink-700">{t('app.billing.no_subscription')}</p>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Saved cards (M7e). Renders nothing when there is no subscription. */}
      <PaymentMethodsCard hasSubscription={sub !== null} />

      {/* Switcher */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-ink-900">{t('app.billing.choose_plan')}</h2>
        <div className="flex items-center gap-3">
          <IntervalToggle interval={interval} onChange={setInterval} t={t} />
          <select
            aria-label={t('app.billing.currency')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="h-10 rounded-xl border border-ink-500/20 bg-white px-3 text-sm text-ink-900"
          >
            {CURRENCIES.map((cur) => (
              <option key={cur} value={cur}>
                {cur}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Coupon */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label htmlFor="coupon" className="text-sm text-ink-700">
          {t('app.billing.coupon_label')}
        </label>
        <input
          id="coupon"
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder={t('app.billing.coupon_placeholder')}
          className="h-10 w-44 rounded-xl border border-ink-500/20 bg-white px-3 text-sm uppercase text-ink-900 placeholder:normal-case placeholder:text-ink-500"
        />
      </div>

      {/* Plan grid */}
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {plansQuery.isLoading ? (
          <>
            <Skeleton className="h-80 w-full" />
            <Skeleton className="h-80 w-full" />
            <Skeleton className="h-80 w-full" />
          </>
        ) : (
          (plansQuery.data ?? []).map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              currency={currency}
              interval={interval}
              isCurrent={sub?.planId === plan.id && sub.status !== 'canceled'}
              onChoose={() =>
                checkout.mutate({ planId: plan.id, currency, interval, couponCode: coupon })
              }
              busy={checkout.isPending}
              t={t}
            />
          ))
        )}
      </div>

      {/* Invoices */}
      {invoices.length > 0 && <InvoicesTable invoices={invoices} locale={locale} />}

      {/* The merged money timeline, behind a disclosure. */}
      {sub && <TransactionsList />}
    </div>
  );
}

function InvoicesTable({ invoices, locale }: { invoices: Invoice[]; locale: 'ar' | 'en' }) {
  const t = useT();
  // The refunded column only earns its width when a row on this page has one.
  const anyRefunded = invoices.some((inv) => (inv.refundedMinor ?? 0) > 0);

  return (
    <div className="mt-12">
      <h2 className="text-lg font-semibold text-ink-900">{t('app.billing.invoices')}</h2>
      <div className="mt-4 overflow-x-auto rounded-2xl bg-white ring-1 ring-burgundy-500/10">
        <table className="w-full text-sm">
          <thead className="bg-cream-100 text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th scope="col" className="px-4 py-3 text-start">
                {t('app.billing.invoice_date')}
              </th>
              <th scope="col" className="px-4 py-3 text-start">
                {t('app.billing.invoice_amount')}
              </th>
              {anyRefunded && (
                <th scope="col" className="px-4 py-3 text-start">
                  {t('app.billing.invoice_refunded_amount')}
                </th>
              )}
              <th scope="col" className="px-4 py-3 text-start">
                {t('app.billing.invoice_status')}
              </th>
              <th scope="col" className="px-4 py-3 text-end">
                <span className="sr-only">{t('app.billing.invoice_view')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-burgundy-500/5">
                <td className="px-4 py-3 tabular-nums text-ink-700">{inv.issuedAt.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <Money amountMinor={inv.amountMinor} currency={inv.currency} emphasis="strong" />
                </td>
                {anyRefunded && (
                  <td className="px-4 py-3">
                    {(inv.refundedMinor ?? 0) > 0 ? (
                      <Money
                        amountMinor={-(inv.refundedMinor ?? 0)}
                        currency={inv.currency}
                        signed
                      />
                    ) : (
                      <span className="text-ink-500">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3">
                  {/* `refunded` / `partially_refunded` became reachable with
                      migration 0011 and had no string, so the badge rendered
                      BLANK. Both keys exist now. */}
                  <Badge tone={invoiceTone(inv.status)}>
                    {t(`app.billing.status_${inv.status}`)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-end">
                  <Link
                    to="/$locale/billing/invoices/$invoiceId"
                    params={{ locale, invoiceId: inv.id }}
                    className="inline-flex min-h-12 items-center rounded-xl px-2 text-sm font-semibold text-burgundy-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy-500"
                  >
                    {t('app.billing.invoice_view')}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CurrentPlanCard({
  planNameKey,
  status,
  periodEnd,
  cancelAtPeriodEnd,
  onCancel,
  onResume,
  busy,
}: {
  planNameKey: string;
  status: string;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  onCancel: () => void;
  onResume: () => void;
  busy: boolean;
}) {
  const t = useT();
  const tone = status === 'active' ? 'success' : status === 'past_due' ? 'warning' : 'neutral';
  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-lg font-bold text-ink-900">{t(planNameKey)}</p>
              <Badge tone={tone}>{t(`app.billing.status_${status}`)}</Badge>
            </div>
            {periodEnd && (
              <p className="mt-1 text-sm text-ink-500">
                {cancelAtPeriodEnd
                  ? t('app.billing.ends_on', { date: periodEnd.slice(0, 10) })
                  : t('app.billing.renews_on', { date: periodEnd.slice(0, 10) })}
              </p>
            )}
          </div>
          {status !== 'canceled' &&
            (cancelAtPeriodEnd ? (
              <Button variant="secondary" size="sm" onClick={onResume} disabled={busy}>
                {t('app.billing.resume')}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
                {t('app.billing.cancel')}
              </Button>
            ))}
        </div>
      </CardBody>
    </Card>
  );
}

function PlanCard({
  plan,
  currency,
  interval,
  isCurrent,
  onChoose,
  busy,
  t,
}: {
  plan: Plan;
  currency: Currency;
  interval: Interval;
  isCurrent: boolean;
  onChoose: () => void;
  busy: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const price = plan.prices.find((p) => p.currency === currency && p.interval === interval);
  return (
    <div
      className={`flex flex-col rounded-3xl bg-white p-6 ring-1 transition-shadow ${
        plan.isHighlighted
          ? 'shadow-[0_24px_60px_-30px_rgba(139,44,61,0.4)] ring-2 ring-burgundy-500'
          : 'ring-burgundy-500/10'
      }`}
    >
      {plan.isHighlighted && (
        <span className="mb-3 inline-flex w-fit rounded-full bg-sun-300 px-3 py-1 text-xs font-bold text-burgundy-700">
          {t('app.billing.most_popular')}
        </span>
      )}
      <h3 className="text-xl font-bold text-ink-900">{t(plan.nameKey)}</h3>
      {plan.taglineKey && <p className="mt-1 text-sm text-ink-500">{t(plan.taglineKey)}</p>}

      <div className="mt-4 flex items-baseline gap-1.5">
        {price ? (
          <>
            <span className="text-sm font-semibold text-ink-500">{currency}</span>
            {/* Formatted from the integer minor units, NOT `displayAmount` —
                one formatter owns every figure on this screen. */}
            <span className="text-3xl font-bold tabular-nums text-burgundy-700">
              {formatMinorAmount(price.amountMinor, currency)}
            </span>
          </>
        ) : (
          <span className="text-3xl font-bold text-burgundy-700">—</span>
        )}
        <span className="text-sm text-ink-500">
          / {t(interval === 'month' ? 'app.billing.per_month' : 'app.billing.per_year')}
        </span>
      </div>

      <ul className="mt-5 flex-1 space-y-2">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-ink-700">
            <span className="mt-0.5 text-leaf" aria-hidden="true">
              ✓
            </span>
            <span>{t(f)}</span>
          </li>
        ))}
      </ul>

      <Button
        className="mt-6"
        fullWidth
        variant={plan.isHighlighted ? 'primary' : 'secondary'}
        disabled={busy || isCurrent || !price}
        onClick={onChoose}
      >
        {isCurrent ? t('app.billing.current_plan') : t('app.billing.choose')}
      </Button>
    </div>
  );
}

function IntervalToggle({
  interval,
  onChange,
  t,
}: {
  interval: Interval;
  onChange: (i: Interval) => void;
  t: (key: string) => string;
}) {
  return (
    <div className="inline-flex rounded-full bg-cream-200 p-1">
      {(['month', 'year'] as Interval[]).map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
            interval === i ? 'bg-white text-burgundy-700 shadow-sm' : 'text-ink-500'
          }`}
        >
          {t(i === 'month' ? 'app.billing.monthly' : 'app.billing.yearly')}
        </button>
      ))}
    </div>
  );
}
